import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Users, Package, Ticket, TrendingUp, Check, X, 
    Shield, Eye, Flame, Loader2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { API } from '../config/apiBase';


export default function AdminPage() {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [pendingDeals, setPendingDeals] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (!isAdmin) {
            toast.error('Acesso restrito a administradores');
            navigate('/');
            return;
        }
        fetchData();
    }, [user, isAdmin, navigate]);

    const fetchData = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        try {
            const [statsRes, pendingRes, usersRes] = await Promise.all([
                fetch(`${API}/admin/stats`, { credentials: 'include', headers }),
                fetch(`${API}/admin/deals/pending`, { credentials: 'include', headers }),
                fetch(`${API}/admin/users`, { credentials: 'include', headers })
            ]);

            if (statsRes.ok) setStats(await statsRes.json());
            if (pendingRes.ok) setPendingDeals(await pendingRes.json());
            if (usersRes.ok) setUsers(await usersRes.json());
        } catch (error) {
            console.error('Error fetching admin data:', error);
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleDealStatus = async (dealId, status, isHot = null) => {
        try {
            const body = { status };
            if (isHot !== null) body.is_hot = isHot;
            
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/produtos/${dealId}/status`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include',
                body: JSON.stringify(body)
            });

            if (response.ok) {
                toast.success(status === 'approved' ? 'Oferta aprovada!' : 'Oferta rejeitada');
                setPendingDeals(pendingDeals.filter(d => d.deal_id !== dealId));
                if (stats) setStats({ ...stats, pending_deals: stats.pending_deals - 1 });
            }
        } catch (error) {
            toast.error('Erro ao atualizar oferta');
        }
    };

    const handleUserRole = async (userId, role) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include',
                body: JSON.stringify({ role })
            });

            if (response.ok) {
                toast.success('Permissao atualizada!');
                setUsers(users.map(u => u.user_id === userId ? { ...u, role, is_approved: role !== 'user' } : u));
            }
        } catch (error) {
            toast.error('Erro ao atualizar usuario');
        }
    };

    const seedData = async () => {
        try {
            const response = await fetch(`${API}/seed`, { method: 'POST' });
            const data = await response.json();
            toast.success(data.message);
            fetchData();
        } catch (error) {
            toast.error('Erro ao popular dados');
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(price);
    };

    if (!user || !isAdmin) {
        return null;
    }

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="admin-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-outfit font-bold flex items-center gap-3" style={{ color: 'var(--text)' }}>
                            <Shield className="w-8 h-8 text-gold-500" />
                            Painel Admin
                        </h1>
                        <p className="mt-1" style={{ color: 'var(--muted-2)' }}>Gerencie ofertas e usuarios</p>
                    </div>
                    <div className="flex gap-3">
                        <Button onClick={seedData} variant="outline" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} data-testid="seed-data-btn">
                            Popular Dados
                        </Button>
                        <Button onClick={fetchData} variant="outline" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} data-testid="refresh-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Atualizar
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-3">
                                <Package className="w-8 h-8 text-gold-500" />
                                <div>
                                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.total_deals}</p>
                                    <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Total de Ofertas</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-3">
                                <Eye className="w-8 h-8 text-gold-500" />
                                <div>
                                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.pending_deals}</p>
                                    <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Pendentes</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-3">
                                <Users className="w-8 h-8 text-blue-500" />
                                <div>
                                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.total_users}</p>
                                    <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Usuarios</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-3">
                                <Ticket className="w-8 h-8 text-green-500" />
                                <div>
                                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.total_coupons}</p>
                                    <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Cupons</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex items-center gap-4 mb-6 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`tab-item flex items-center gap-2 pb-2 text-sm font-medium ${activeTab === 'pending' ? 'active' : ''}`}
                        data-testid="tab-pending"
                    >
                        <Eye className="w-4 h-4" />
                        Ofertas Pendentes
                        {pendingDeals.length > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--gold-2)', color: 'var(--sv-on-accent)' }}>
                                {pendingDeals.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`tab-item flex items-center gap-2 pb-2 text-sm font-medium ${activeTab === 'users' ? 'active' : ''}`}
                        data-testid="tab-users"
                    >
                        <Users className="w-4 h-4" />
                        Usuarios
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
                    </div>
                ) : activeTab === 'pending' ? (
                    <div className="space-y-4">
                        {pendingDeals.length === 0 ? (
                            <div className="text-center py-16">
                                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                <p style={{ color: 'var(--muted-2)' }}>Nenhuma oferta pendente de aprovacao</p>
                            </div>
                        ) : (
                            pendingDeals.map((deal) => (
                                <div 
                                    key={deal.deal_id} 
                                    className="rounded-xl p-3 sm:p-4"
                                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                                    data-testid={`pending-deal-${deal.deal_id}`}
                                >
                                    {/* Top: image + info */}
                                    <div className="flex gap-3 mb-3">
                                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0" style={{ background: 'var(--surface-2)' }}>
                                            <img src={deal.image_url} alt={deal.title} className="w-full h-full object-cover" onError={e => { e.target.onerror=null; e.target.src='data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" fill="none"><rect width="96" height="96" rx="12" fill="%231a1a1a"/><text x="50%" y="54%" text-anchor="middle" fill="%23555" font-size="11" font-family="sans-serif">Sem foto</text></svg>') }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] mb-0.5 truncate" style={{ color: 'var(--muted-2)' }}>{deal.store_name}</p>
                                            <h3 className="text-sm font-medium line-clamp-2 leading-snug" style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{deal.title}</h3>
                                            <div className="flex items-baseline gap-2 mt-1.5 flex-wrap">
                                                <span className="text-base font-bold" style={{ color: 'var(--text)' }}>{formatPrice(deal.current_price)}</span>
                                                {deal.original_price > deal.current_price && (
                                                    <span className="text-[10px] line-through" style={{ color: 'var(--muted-2)' }}>{formatPrice(deal.original_price)}</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--muted-2)' }}>Por: {deal.author_name}</p>
                                        </div>
                                    </div>
                                    {/* Bottom: action buttons - horizontal on mobile */}
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={() => handleDealStatus(deal.deal_id, 'approved')}
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700 text-white flex-1 text-xs"
                                            data-testid={`approve-deal-${deal.deal_id}`}
                                        >
                                            <Check className="w-3 h-3 mr-1" />
                                            Aprovar
                                        </Button>
                                        <Button
                                            onClick={() => handleDealStatus(deal.deal_id, 'approved', true)}
                                            size="sm"
                                            variant="outline"
                                            className="border-orange-500/50 text-orange-500 flex-1 text-xs"
                                            data-testid={`approve-hot-deal-${deal.deal_id}`}
                                        >
                                            <Flame className="w-3 h-3 mr-1" />
                                            HOT
                                        </Button>
                                        <Button
                                            onClick={() => handleDealStatus(deal.deal_id, 'rejected')}
                                            size="sm"
                                            variant="outline"
                                            className="border-red-500/50 text-red-400 flex-1 text-xs"
                                            data-testid={`reject-deal-${deal.deal_id}`}
                                        >
                                            <X className="w-3 h-3 mr-1" />
                                            Rejeitar
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {users.map((u) => (
                            <div 
                                key={u.user_id} 
                                className="rounded-xl p-4 flex items-center gap-4"
                                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
                                data-testid={`user-${u.user_id}`}
                            >
                                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'var(--surface-2)' }}>
                                    {u.picture ? (
                                        <img src={resolveImageUrl(u.picture)} alt={u.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gold-500 font-bold">
                                            {u.name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate" style={{ color: 'var(--text)' }}>{u.name}</p>
                                    <p className="text-xs truncate" style={{ color: 'var(--muted-2)' }}>{u.email}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-1 rounded ${
                                        u.role === 'admin' 
                                            ? 'bg-red-500/20 text-red-400' 
                                            : u.role === 'approved' 
                                                ? 'bg-green-500/20 text-green-400'
                                                : ''
                                    }`} style={u.role !== 'admin' && u.role !== 'approved' ? { background: 'var(--surface-2)', color: 'var(--muted)' } : {}}>
                                        {u.role === 'admin' ? 'Admin' : u.role === 'approved' ? 'Aprovado' : 'Usuario'}
                                    </span>
                                    
                                    {u.role !== 'admin' && (
                                        <div className="flex gap-1">
                                            {u.role !== 'approved' && (
                                                <Button
                                                    onClick={() => handleUserRole(u.user_id, 'approved')}
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-green-500/50 text-green-400 text-xs"
                                                    data-testid={`approve-user-${u.user_id}`}
                                                >
                                                    Aprovar
                                                </Button>
                                            )}
                                            {u.role === 'approved' && (
                                                <Button
                                                    onClick={() => handleUserRole(u.user_id, 'user')}
                                                    size="sm"
                                                    variant="outline"
                                                    style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
                                                    className="text-xs"
                                                >
                                                    Remover
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
